// Portuguese location codes are hierarchical by prefix: distrito = 2 digits,
// concelho = 4, freguesia = 6 (matches `administrativeLevelForCode`). So a
// freguesia code starts with its concelho code, which starts with its district
// code. We use that to find the trail (district › county › parish) common to a
// set of zones without needing parent links.

import { normalizeUrl } from "@/lib/utils";

type Location = {
  code?: string | null;
  name?: string | null;
  normalizedName?: string | null;
};

type TrailPart = { value: string; label: string };

// The ancestor chain of a code, deepest-first kept in order: [district,
// concelho, freguesia], trimmed to the code's own level.
function chainForCode(code: string): string[] {
  const chain: string[] = [];
  if (code.length >= 2) chain.push(code.slice(0, 2));
  if (code.length >= 4) chain.push(code.slice(0, 4));
  if (code.length > 4) chain.push(code);
  return chain;
}

// Codes that aren't a prefix-ancestor of another (longer) code in the set —
// i.e. the actual selected zones, dropping the ancestors the API includes.
function leafCodes(codes: string[]): string[] {
  const unique = Array.from(new Set(codes));
  return unique.filter(
    (c) =>
      !unique.some((o) => o !== c && o.length > c.length && o.startsWith(c)),
  );
}

/**
 * The location trail common to every zone in `locations`, as
 * `[district, county, parish]` truncated at the deepest shared level. Returns
 * `[]` when the zones don't even share a district.
 */
export function commonLocationTrail(locations: Location[]): TrailPart[] {
  const nameByCode = new Map<string, string>();
  for (const l of locations) {
    if (l?.code && l?.name) nameByCode.set(l.code, l.name);
  }

  const codes = locations
    .map((l) => l?.code)
    .filter((c): c is string => !!c);
  const leaves = leafCodes(codes);
  if (leaves.length === 0) return [];

  const chains = leaves.map(chainForCode);
  const shortest = Math.min(...chains.map((c) => c.length));

  const commonCodes: string[] = [];
  for (let i = 0; i < shortest; i++) {
    const code = chains[0][i];
    if (chains.every((c) => c[i] === code)) commonCodes.push(code);
    else break;
  }

  return commonCodes
    .map((code) => ({ value: code, label: nameByCode.get(code) }))
    .filter((p): p is TrailPart => !!p.label);
}

/** How many distinct zones the listing targets (multi-zone demand → > 1). */
export function leafZoneCount(locations: Location[]): number {
  return leafCodes(
    locations.map((l) => l?.code).filter((c): c is string => !!c),
  ).length;
}

export type LocationBreadcrumb = {
  /** The shared trail (district › county › parish), truncated at the deepest
   *  common level. */
  parts: TrailPart[];
  /** When the listing's zones diverge below `parts`, the admin-level index they
   *  diverge at (0 = district, 1 = county, 2 = parish) — render a
   *  "Multiple {level}s" tail. `null` for a single, fully-resolved location. */
  multiLevel: 0 | 1 | 2 | null;
};

/**
 * Breadcrumb for a listing: the trail common to all its zones, plus a
 * "multiple" marker at the first level where the zones differ. Examples:
 * one location → `[d,c,p]`, no marker; zones across one district →
 * `[district]` + "multiple counties"; zones across districts → `[]` +
 * "multiple districts".
 */
export function locationBreadcrumb(locations: Location[]): LocationBreadcrumb {
  const parts = commonLocationTrail(locations);
  if (leafZoneCount(locations) <= 1) return { parts, multiLevel: null };
  // Multiple zones diverge right below the shared trail. Sharing a parish would
  // mean the same zone, so parts.length is at most 2 here.
  const level = parts.length;
  return { parts, multiLevel: level <= 2 ? (level as 0 | 1 | 2) : null };
}

/**
 * A valid `[district, county, parish]` normalized-name path for the listing's
 * canonical SEO route, which always needs all three segments. For a multi-zone
 * demand (no single canonical location) the **first** freguesia-level zone's
 * trail is used as a representative — the detail page resolves by slug anyway,
 * and its breadcrumb shows "multiple locations". Returns `null` when no zone has
 * a full three-level trail (so callers can avoid a broken route).
 */
// Placeholder segments for levels where a multi-zone demand has no single
// value, indexed by level (0 district, 1 county, 2 parish). They keep the
// detail URL 4-deep (so it never collides with the 1–3 segment listing pages)
// while mirroring the breadcrumb's "Multiple {level}s".
export const MULTI_LOCATION_TOKENS = [
  "multiple-districts",
  "multiple-counties",
  "multiple-parishes",
] as const;

/** True for a placeholder segment produced by `detailPathForListing`. */
export function isMultiLocationToken(segment: string): boolean {
  return (MULTI_LOCATION_TOKENS as readonly string[]).includes(segment);
}

/**
 * Canonical detail path for a listing, always `/district/county/parish/slug`.
 * Levels common to all the listing's zones use the real normalized name; each
 * level below the common prefix uses *its own* placeholder, so the URL keeps the
 * real location as far as it's shared and never repeats a token:
 *   single            → /coimbra/coimbra/marvila/slug
 *   across parishes   → /coimbra/coimbra/multiple-parishes/slug
 *   across concelhos  → /coimbra/multiple-counties/multiple-parishes/slug
 *   across districts  → /multiple-districts/multiple-counties/multiple-parishes/slug
 * Always 4-deep (unambiguous vs the 1–3 segment listing pages); the page
 * resolves by slug. Returned without locale prefix or origin — callers add those.
 */
export function detailPathForListing(
  locations: Location[],
  slug: string,
): string {
  const { parts } = locationBreadcrumb(locations);

  const normByCode = new Map<string, string>();
  for (const l of locations) {
    if (l?.code && l?.normalizedName) normByCode.set(l.code, l.normalizedName);
  }

  const segments = Array.from({ length: 3 }, (_, level) => {
    const code = parts[level]?.value;
    const name = code ? normByCode.get(code) : undefined;
    // Slugify the location name so multi-word parishes (e.g. "uniao das
    // freguesias de ...") become hyphenated path segments instead of leaking
    // raw spaces (which a shared/copied URL encodes as %20).
    return name ? normalizeUrl(name) : MULTI_LOCATION_TOKENS[level];
  });

  return `/${segments.join("/")}/${slug}`;
}

export function canonicalLocationPath(locations: Location[]): string[] | null {
  const normByCode = new Map<string, string>();
  for (const l of locations) {
    if (l?.code && l?.normalizedName) normByCode.set(l.code, l.normalizedName);
  }

  const leaves = leafCodes(
    locations.map((l) => l?.code).filter((c): c is string => !!c),
  );

  for (const leaf of leaves) {
    if (leaf.length <= 4) continue; // not a freguesia → no full trail
    const trail = [leaf.slice(0, 2), leaf.slice(0, 4), leaf].map((c) =>
      normByCode.get(c),
    );
    // Slugify segments so multi-word names don't leak spaces into the URL.
    if (trail.every((s): s is string => !!s))
      return trail.map((s) => normalizeUrl(s));
  }
  return null;
}
